import { useMemo } from 'react'
import { Spinner } from '../../../components/atoms/Spinner'
import { BuildCheckboxList } from './BuildCheckboxList'
import { BuildPriceSlider } from './BuildPriceSlider'
import { useBuildFilterMeta } from '../../../../viewmodels/useBuildFilterMeta'
import { useBuildFilterAttributes } from '../../../../viewmodels/useBuildFilterAttributes'
import { useBuildPriceRange } from '../../../../viewmodels/useBuildPriceRange'
import type { BuildProductsFilter } from '../../../../viewmodels/useBuildProducts'

/**
 * BuildFilterPanel — parallel to ShopFilterPanel. Same three sections
 * (price slider, category multi-select, per-attribute CDT lists) on the
 * walnut panel.
 */
interface Props {
  readonly filter: BuildProductsFilter
  readonly onFiltersChange: (next: BuildProductsFilter) => void
}

export function BuildFilterPanel({ filter, onFiltersChange }: Props): JSX.Element {
  const meta = useBuildFilterMeta()
  const attrs = useBuildFilterAttributes(filter.categoryIds)
  const dynamicRange = useBuildPriceRange(filter.categoryIds)
  const selectedCategoryIds = useMemo(() => new Set(filter.categoryIds), [filter.categoryIds])

  const liveMin = dynamicRange.priceRange.min_paisa ?? meta.priceRange.min_paisa
  const liveMax = dynamicRange.priceRange.max_paisa ?? meta.priceRange.max_paisa
  const hasRange = liveMin !== null && liveMax !== null && liveMax > liveMin
  const minBound = liveMin ?? 0
  const maxBound = liveMax ?? 1
  const valueMin = filter.priceMinPaisa ?? minBound
  const valueMax = filter.priceMaxPaisa ?? maxBound

  function toggleCategory(id: string): void {
    const exists = selectedCategoryIds.has(id)
    const next = exists
      ? filter.categoryIds.filter((c) => c !== id)
      : [...filter.categoryIds, id]
    onFiltersChange({ ...filter, categoryIds: next })
  }

  function toggleAttributeValue(attrId: string, valueId: string): void {
    const current = filter.attributeValues[attrId] ?? []
    const exists = current.includes(valueId)
    const nextValues = exists
      ? current.filter((v) => v !== valueId)
      : [...current, valueId]
    const nextAttrs = { ...filter.attributeValues }
    if (nextValues.length === 0) delete nextAttrs[attrId]
    else nextAttrs[attrId] = nextValues
    onFiltersChange({ ...filter, attributeValues: nextAttrs })
  }

  function commitPrice(next: { minPaisa: number; maxPaisa: number }): void {
    onFiltersChange({
      ...filter,
      priceMinPaisa: next.minPaisa === minBound ? null : next.minPaisa,
      priceMaxPaisa: next.maxPaisa === maxBound ? null : next.maxPaisa
    })
  }

  const sectionClass = 'rounded-xl border border-text-light/15 bg-text-light/5 p-4 shadow-sm'
  const headingClass = 'mb-3 font-heading text-h4 text-text-light'
  const mutedClass = 'font-body text-body-xs text-text-light/70'

  return (
    <aside className="w-full space-y-5">
      <section className={sectionClass}>
        <h3 className={headingClass}>
          Price
          {filter.categoryIds.length > 0 ? (
            <span className={`ml-2 ${mutedClass}`}>
              (in selected categories)
            </span>
          ) : null}
        </h3>
        {meta.isLoading && !hasRange ? (
          <div className="flex items-center justify-center py-3"><Spinner /></div>
        ) : !hasRange ? (
          <p className={mutedClass}>
            {filter.categoryIds.length > 0
              ? 'No priced products in the selected categories.'
              : 'No priced products yet.'}
          </p>
        ) : (
          <BuildPriceSlider
            catalogMinPaisa={minBound}
            catalogMaxPaisa={maxBound}
            valueMinPaisa={valueMin}
            valueMaxPaisa={valueMax}
            onCommit={commitPrice}
            disabled={dynamicRange.isLoading}
          />
        )}
      </section>

      <section className={sectionClass}>
        <h3 className={headingClass}>Category</h3>
        {meta.isLoading ? (
          <div className="flex items-center justify-center py-3"><Spinner /></div>
        ) : (
          <BuildCheckboxList
            options={meta.categories.map((c) => ({ id: c.id, label: c.name, subLabel: c.product_count }))}
            selectedIds={selectedCategoryIds}
            onToggle={toggleCategory}
            emptyNote="No categories yet."
          />
        )}
      </section>

      {filter.categoryIds.length > 0 ? (
        attrs.isLoading ? (
          <div className={sectionClass}>
            <div className="flex items-center justify-center py-3"><Spinner /></div>
          </div>
        ) : attrs.groups.length === 0 ? null : (
          attrs.groups.map((g) => {
            const selected = new Set(filter.attributeValues[g.attribute_id] ?? [])
            return (
              <section key={g.attribute_id} className={sectionClass}>
                <h3 className={headingClass}>{g.attribute_label}</h3>
                <BuildCheckboxList
                  options={g.values.map((v) => ({ id: v.id, label: v.label }))}
                  selectedIds={selected}
                  onToggle={(vid) => toggleAttributeValue(g.attribute_id, vid)}
                />
              </section>
            )
          })
        )
      ) : null}
    </aside>
  )
}
